/**
 * Plan-restricted checkout coupons.
 *
 * Chargebee holds the real discount. This file mirrors the coupons whose Chargebee
 * `item_constraints` limit them to specific item prices, so that:
 *   1. the payment routes never send such a coupon with any other plan (Chargebee
 *      rejects the whole subscription create with "not applicable to any of the
 *      items", which would fail the checkout), and
 *   2. the setup page shows the exact first-cycle total Chargebee will invoice
 *      (display <-> charge parity; process_nmi_renewal charges the invoice amount_due).
 *
 * Coupons not listed here (COMEBACK20 / COMEBACK30) apply to any plan and are unaffected.
 */

export interface PlanRestrictedCoupon {
    /** Chargebee item price ids the coupon applies to (Chargebee item_constraints, plan = specific). */
    planIds: ReadonlySet<string>
    /** First-cycle invoice total in cents after the discount (list price minus the Chargebee fixed amount). */
    firstCycleTotalCents: number
}

/**
 * psexpert: Chargebee coupon `psexpert` — fixed $470.00 off `plan-glory-1188` ($1,188 -> $718),
 * duration_type one_time (first invoice only; renewals at list price), no expiry, unlimited redemptions.
 * Not advertised anywhere; only reaches /setup through the partner-migration redirect links.
 * Since the 2026-10-24 parity pricing, Glory yearly is plan-glory-720 ($720), which this coupon does
 * NOT apply to: migration links carrying coupon=psexpert are stripped at checkout and buy Glory
 * yearly at list $720 (within $2 of the old $718 promise). Kept so stale links never fail.
 */
export const PLAN_RESTRICTED_COUPONS: Readonly<Record<string, PlanRestrictedCoupon>> = {
    psexpert: { planIds: new Set(["plan-glory-1188"]), firstCycleTotalCents: 71800 },
}

/** True when the coupon may be sent to Chargebee with this plan (unrestricted coupons always pass). */
export function isCouponApplicableToPlan(coupon: string | null | undefined, planId: string | null | undefined): boolean {
    if (!coupon) return false
    const restricted = PLAN_RESTRICTED_COUPONS[coupon]
    if (!restricted) return true
    return !!planId && restricted.planIds.has(planId)
}

/** First-cycle total (cents) the coupon produces on this plan, or null when it does not change the price. */
export function getCouponFirstCycleTotalCents(coupon: string | null | undefined, planId: string | null | undefined): number | null {
    if (!coupon || !planId) return null
    const restricted = PLAN_RESTRICTED_COUPONS[coupon]
    return restricted && restricted.planIds.has(planId) ? restricted.firstCycleTotalCents : null
}

/**
 * Affiliate partner coupons (affiliate program, founder terms 2026-10-25: commission-only,
 * 25% recurring standard / 30% for exclusive placements). One code per launch partner so every
 * checkout is attributable. Each mirrors the Chargebee coupon COMEBACK20 exactly: percentage 20,
 * duration_type one_time (first invoice only; renewals at list), apply_on invoice_amount, no
 * item_constraints (fits every plan, so it is NOT in PLAN_RESTRICTED_COUPONS and never stripped
 * by plan). One coupon per checkout (no stacking), same as every other coupon.
 * Wallet checkouts never attach a non-plan-restricted coupon (7b521b89, Day-0 wallet sale is priced
 * from planId): a wallet buyer with a partner code pays list and is still attributed
 * (billing_events metadata `coupon` + `coupon_applied:false`).
 * The Chargebee coupon object MUST exist before a code is allowlisted in production, or Chargebee
 * rejects the subscription create and the checkout fails. Ids are case-sensitive.
 * Attribution read: scripts/affiliate-attribution.mjs.
 */
export interface AffiliateCoupon {
    /** Internal partner label (never shown to customers). */
    partner: string
    /** Percent off the first invoice (mirrors the Chargebee coupon). */
    discountPercent: number
}

export const AFFILIATE_COUPONS: Readonly<Record<string, AffiliateCoupon>> = {
    "partner-platypus-20": { partner: "platypus", discountPercent: 20 },
    "partner-howsociable-20": { partner: "howsociable", discountPercent: 20 },
    "partner-creatortribune-20": { partner: "creatortribune", discountPercent: 20 },
}

export const AFFILIATE_COUPON_CODES: readonly string[] = Object.keys(AFFILIATE_COUPONS)

/** Partner label for an affiliate coupon, or null for any other code. */
export function affiliatePartnerForCoupon(coupon: string | null | undefined): string | null {
    return (coupon && AFFILIATE_COUPONS[coupon]?.partner) || null
}

/** billing_events metadata recording which coupon a checkout carried and whether Chargebee got it. */
export function couponEventMetadata(coupon: string | null | undefined, applied: boolean): Record<string, string | boolean> {
    if (!coupon) return {}
    const partner = affiliatePartnerForCoupon(coupon)
    return { coupon, coupon_applied: applied, ...(partner ? { affiliate_partner: partner } : {}) }
}
